import {Component, OnInit} from '@angular/core';
import {ApiService} from '../../services/api.service';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faPlus, faSquareCheck, faSquarePlus, faSquareXmark, faTrash} from '@fortawesome/free-solid-svg-icons';
import {CommonModule} from '@angular/common';
import {forkJoin, Observable, of, switchMap} from 'rxjs';
import {
    VEHICLE_MONITORING_DEFAULT_SEVERITY,
    VEHICLE_MONITORING_SEVERITY_OPTIONS,
    VEHICLE_MONITORING_TYPES,
} from '../../shared/vehicle-monitoring/vehicle-monitoring.constants';

type ExistingRuleSnapshot = {
    id: number;
    alertType: string;
    severity: string;
    remarks: string;
};

@Component({
    selector: 'app-edit-vehicle-monitoring',
    imports: [CommonModule, ReactiveFormsModule, FontAwesomeModule],
    templateUrl: './edit-vehicle-monitoring.component.html',
    styleUrl: './edit-vehicle-monitoring.component.css'
})
export class EditVehicleMonitoringComponent implements OnInit {
    vehicleForm: FormGroup;
    isEditMode = false;
    pageTitle = 'Add Vehicle';
    submitLabel = 'Add Vehicle';
    loading = false;
    saving = false;
    groupId: number | null = null;
    existingRules: ExistingRuleSnapshot[] = [];

    alertTypeOptions = VEHICLE_MONITORING_TYPES;
    severityOptions = VEHICLE_MONITORING_SEVERITY_OPTIONS;

    constructor(
        private apiService: ApiService,
        private router: Router,
        private route: ActivatedRoute,
        private toastr: ToastrService,
        private fb: FormBuilder,
        library: FaIconLibrary,
    ) {
        library.addIcons(faSquareXmark, faSquarePlus, faSquareCheck, faTrash, faPlus);

        this.vehicleForm = this.fb.group({
            vehicleNumber: ['', Validators.required],
            rules: this.fb.array([]),
        });
    }

    ngOnInit() {
        this.route.paramMap.subscribe((params: ParamMap) => {
            this.initializeFromRoute(params);
        });
    }

    get rules(): FormArray {
        return this.vehicleForm.get('rules') as FormArray;
    }

    private initializeFromRoute(params: ParamMap) {
        this.isEditMode = false;
        this.pageTitle = 'Add Vehicle';
        this.submitLabel = 'Add Vehicle';
        this.groupId = null;
        this.existingRules = [];
        this.vehicleForm.reset({vehicleNumber: ''});
        this.rules.clear();
        this.vehicleForm.get('vehicleNumber')?.enable();
        this.addRuleRow();

        const idParam = params.get('id') ?? params.get('pk');
        if (!idParam || !/^\d+$/.test(idParam)) {
            return;
        }

        this.isEditMode = true;
        this.pageTitle = 'Edit Vehicle';
        this.submitLabel = 'Update Vehicle';
        this.groupId = Number(idParam);
        this.vehicleForm.get('vehicleNumber')?.disable();
        this.loadVehicleMonitoring(this.groupId);
    }

    private loadVehicleMonitoring(id: number) {
        this.loading = true;
        this.apiService.getVehicleMonitoringDetail(id).subscribe({
            next: (record: any) => {
                this.loading = false;
                this.groupId = record.id;
                this.rules.clear();
                this.existingRules = [];
                this.vehicleForm.patchValue({vehicleNumber: record.vehicle_number});

                const monitoringRules = record.monitoring_rules ?? [];
                if (!monitoringRules.length) {
                    this.addRuleRow();
                    return;
                }

                monitoringRules.forEach((rule: any) => {
                    this.existingRules.push({
                        id: rule.id,
                        alertType: rule.type,
                        severity: rule.severity || VEHICLE_MONITORING_DEFAULT_SEVERITY,
                        remarks: rule.remarks || '',
                    });
                    this.rules.push(this.createRuleGroup({
                        id: rule.id,
                        alertType: rule.type,
                        severity: rule.severity || VEHICLE_MONITORING_DEFAULT_SEVERITY,
                        remarks: rule.remarks || '',
                    }));
                });
            },
            error: (err: any) => {
                this.loading = false;
                this.handleError(err);
                this.router.navigate(['/vehicle-monitoring']);
            },
        });
    }

    private createRuleGroup(value?: {
        id?: number | null;
        alertType?: string;
        severity?: string;
        remarks?: string;
    }): FormGroup {
        return this.fb.group({
            id: [value?.id ?? null],
            alertType: [value?.alertType ?? '', Validators.required],
            severity: [value?.severity ?? VEHICLE_MONITORING_DEFAULT_SEVERITY, Validators.required],
            remarks: [value?.remarks ?? ''],
        });
    }

    addRuleRow() {
        this.rules.push(this.createRuleGroup());
    }

    removeRuleRow(index: number) {
        if (this.rules.length === 1) {
            return;
        }
        this.rules.removeAt(index);
    }

    availableAlertTypes(currentIndex: number): {value: string; label: string}[] {
        const selected = this.rules.controls
            .map((control, index) => index === currentIndex ? '' : control.get('alertType')?.value)
            .filter(Boolean);
        return this.alertTypeOptions.filter(option => !selected.includes(option.value));
    }

    onSubmit() {
        if (!this.vehicleForm.valid) {
            this.validateAllFormFields(this.vehicleForm);
            return;
        }

        const vehicleNumber = this.vehicleForm.getRawValue().vehicleNumber?.trim().toUpperCase();
        const ruleValues = this.rules.getRawValue();

        if (!ruleValues.length) {
            this.toastr.error('Add at least one monitoring type.', 'Error');
            return;
        }

        const alertTypes = ruleValues.map((rule: any) => rule.alertType);
        if (new Set(alertTypes).size !== alertTypes.length) {
            this.toastr.error('Each monitoring type can only be selected once.', 'Error');
            return;
        }

        this.saving = true;

        if (!this.isEditMode) {
            const createRequests = ruleValues.map((rule: any) =>
                this.apiService.addVehicleAlertRule({
                    vehicle_number: vehicleNumber,
                    alert_type: rule.alertType,
                    severity: rule.severity || VEHICLE_MONITORING_DEFAULT_SEVERITY,
                    ...(rule.remarks ? {remarks: rule.remarks} : {}),
                }),
            );
            forkJoin(createRequests).subscribe({
                next: () => this.finishSave('Vehicle added to monitoring successfully.'),
                error: (err: any) => {
                    this.saving = false;
                    this.handleError(err);
                },
            });
            return;
        }

        const updateRequests: Observable<unknown>[] = [];
        const submittedIds = new Set<number>();
        const removedRuleIds: number[] = [];

        for (const rule of ruleValues) {
            const payload = {
                alert_type: rule.alertType,
                severity: rule.severity || VEHICLE_MONITORING_DEFAULT_SEVERITY,
                remarks: rule.remarks || null,
            };
            if (rule.id) {
                submittedIds.add(rule.id);
                const existing = this.existingRules.find(item => item.id === rule.id);
                const changed = !existing
                    || existing.alertType !== rule.alertType
                    || existing.severity !== payload.severity
                    || (existing.remarks || '') !== (payload.remarks || '');
                if (changed) {
                    updateRequests.push(this.apiService.updateVehicleAlertRule(rule.id, payload));
                }
            } else {
                updateRequests.push(this.apiService.addVehicleAlertRule({
                    vehicle_number: vehicleNumber,
                    ...payload,
                }));
            }
        }

        for (const existing of this.existingRules) {
            if (!submittedIds.has(existing.id)) {
                removedRuleIds.push(existing.id);
            }
        }

        const deleteRequest$ = removedRuleIds.length
            ? this.apiService.deleteVehicleMonitoringRules(removedRuleIds)
            : of(null);

        if (!updateRequests.length && !removedRuleIds.length) {
            this.finishSave('Vehicle monitoring record updated successfully.');
            return;
        }

        deleteRequest$.pipe(
            switchMap(() => updateRequests.length ? forkJoin(updateRequests) : of(null)),
        ).subscribe({
            next: () => this.finishSave('Vehicle monitoring record updated successfully.'),
            error: (err: any) => {
                this.saving = false;
                this.handleError(err);
            },
        });
    }

    private finishSave(message: string) {
        this.saving = false;
        this.toastr.success(message, 'Success');
        this.router.navigate(['/vehicle-monitoring']);
    }

    handleError(err: any) {
        if (err?.error?.alert_type) {
            err.error.alert_type.forEach((message: string) => this.toastr.error(message, 'Error'));
        } else if (err?.error?.vehicle_number) {
            err.error.vehicle_number.forEach((message: string) => this.toastr.error(message, 'Error'));
        } else if (err?.error?.detail) {
            this.toastr.error(err.error.detail, 'Error');
        } else if (err?.error?.non_field_errors) {
            err.error.non_field_errors.forEach((message: string) => this.toastr.error(message, 'Error'));
        } else if (err?.status !== 403 && err.status !== 0) {
            this.toastr.error('Something went wrong!', 'Error');
        }
    }

    validateAllFormFields(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach(field => {
            const control = formGroup.get(field);
            if (control instanceof FormGroup) {
                this.validateAllFormFields(control);
            } else if (control instanceof FormArray) {
                control.controls.forEach(item => {
                    if (item instanceof FormGroup) {
                        this.validateAllFormFields(item);
                    }
                });
            } else {
                control?.markAsTouched({onlySelf: true});
            }
        });
    }

    labelForAlertType(value: string): string {
        return this.alertTypeOptions.find(option => option.value === value)?.label ?? value;
    }

    onCancel() {
        this.router.navigate(['/vehicle-monitoring']);
    }
}
